import React from 'react';
import { TooltipText, TooltipHeading } from '../Layout';
// import { ContentConfig, GetBorderRadiusOptions, TooltipLink } from '../../../Tooltip/types';
import { ContentConfig, GetBorderRadiusOptions, TooltipLink } from '../types';
// =============================================================================
// RE-EXPORTS
// =============================================================================

export { autoSafeFlip, ARROW_PADDING } from './autoSafeFlip';
export type { ContentConfig } from '../types';


// =============================================================================
// CONSTANTS
// =============================================================================

export const BORDER_RADIUS = '0.8rem';
export const CURSOR_OFFSET = 10;
export const VIEWPORT_PADDING = 8;
export const CURSOR_HEIGHT = 20;

// =============================================================================
// CONTENT RENDERING
// =============================================================================

/**
 * Render tooltip content based on layout configuration
 */
export const renderContent = (config: ContentConfig): React.ReactNode => {
    switch (config.layout) {
        case 'TooltipText':
            return React.createElement(TooltipText, config.props);
        case 'TooltipHeading':
            return React.createElement(TooltipHeading, config.props);
        case 'custom':
            return config.render;
        default:
            return null;
    }
};

/**
 * Resolve primary content from shorthand props (text/heading) or explicit config
 */
export const resolvePrimaryContent = (
    text?: string,
    heading?: string,
    primaryContent?: ContentConfig
): ContentConfig | null => {
    if (primaryContent) return primaryContent;

    if (heading && text) {
        return { layout: 'TooltipHeading', props: { heading, text } };
    }

    if (text) {
        return { layout: 'TooltipText', props: { text } };
    }

    return null;
};

// =============================================================================
// BORDER RADIUS CALCULATION
// =============================================================================

/**
 * Calculate border radius for progressive tooltips where primary and secondary
 * content need to visually merge when gap is 0
 */
export const getBorderRadius = ({
    isPrimary,
    progressiveGap,
    secondaryContent,
    showSecondary,
    placement,
}: GetBorderRadiusOptions): string => {
    const hasGap = progressiveGap > 0;
    if (hasGap || !secondaryContent || !showSecondary) return BORDER_RADIUS;

    const isTopPlacement = placement.startsWith('top');
    const isLeftPlacement = placement.startsWith('left');
    const isVertical = isTopPlacement || placement.startsWith('bottom');
    const secondaryFirst = isTopPlacement || isLeftPlacement;

    if (isPrimary) {
        if (isVertical) {
            return secondaryFirst
                ? `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`
                : `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`;
        }
        return secondaryFirst
            ? `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`
            : `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`;
    }

    if (isVertical) {
        return secondaryFirst
            ? `${BORDER_RADIUS} ${BORDER_RADIUS} 0 0`
            : `0 0 ${BORDER_RADIUS} ${BORDER_RADIUS}`;
    }
    return secondaryFirst
        ? `${BORDER_RADIUS} 0 0 ${BORDER_RADIUS}`
        : `0 ${BORDER_RADIUS} ${BORDER_RADIUS} 0`;
};

// =============================================================================
// LINK RENDERING
// =============================================================================

/**
 * Simple link renderer (without OKELink dependency)
 */
export const renderLink = (link: TooltipLink, linkStyle?: React.CSSProperties) => {
    return (
        <a
            href={link.href}
            target={link.newTab ? '_blank' : undefined}
            rel={link.newTab ? 'noopener noreferrer' : undefined}
            style={{
                color: 'inherit',
                textDecoration: 'underline',
                ...linkStyle,
            }}
        >
            {link.text}
        </a>
    );
};
